/**
 * kanban/KanbanCard.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * A single task or milestone card rendered inside a Kanban column.
 */

import React, { useState } from 'react';
import { Tooltip } from 'antd';
import {
    getInitials,
    getFullName,
    getPhotoUrl,
    parseEstimatedToMs,
    formatMs,
} from './kanbanUtils';
import { PRIORITY_BADGE, EFFORT_BAR_COLOR } from './constants';

function AssigneeCell({ assignee }) {
    const [imgError, setImgError] = useState(false);

    if (!assignee) {
        return (
            <div className="flex items-center gap-1.5">
                <div className="w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-xs font-bold text-slate-400 flex-shrink-0 overflow-hidden">
                    ?
                </div>
                <span className="text-xs font-medium text-[#737782] dark:text-slate-500">
                    Unassigned
                </span>
            </div>
        );
    }

    const fullName = getFullName(assignee);
    const parts = fullName.trim().split(/\s+/);
    const shortName = parts.length >= 2
        ? `${parts[0]} ${parts[parts.length - 1][0]}.`
        : fullName;

    const photoUrl = getPhotoUrl(assignee?.photo);
    const showPhoto = photoUrl && !imgError;

    return (
        <Tooltip title={fullName}>
            <div className="flex items-center gap-1.5">
                <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 overflow-hidden ring-1 ring-white/60 dark:ring-slate-700"
                    style={showPhoto ? {} : { background: 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)' }}
                >
                    {showPhoto ? (
                        <img
                            src={photoUrl}
                            alt={fullName}
                            className="w-full h-full object-cover rounded-full"
                            onError={() => setImgError(true)}
                        />
                    ) : (
                        getInitials(fullName)
                    )}
                </div>
                <span className="text-xs font-medium text-[#424750] dark:text-slate-400 truncate max-w-[80px]">
                    {shortName}
                </span>
            </div>
        </Tooltip>
    );
}

function EffortBar({ estimatedTime, actualTimeMs, status, isMilestone, progress }) {
    if (isMilestone) {
        return (
            <div className="mt-2 mb-1">
                <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                        className="h-full rounded-full transition-all duration-300 bg-emerald-500"
                        style={{ width: `${progress ?? 0}%` }}
                    />
                </div>
            </div>
        );
    }

    const estimatedMs = parseEstimatedToMs(estimatedTime);
    const actual = actualTimeMs || 0;
    const pct = estimatedMs > 0 ? Math.min(Math.round((actual / estimatedMs) * 100), 100) : 0;
    const overrun = estimatedMs > 0 && actual > estimatedMs;

    if (!estimatedTime && !actual) return null;

    const barColor = overrun ? 'bg-red-400' : (EFFORT_BAR_COLOR[status] || 'bg-[#28b5fd]');

    return (
        <div className="mt-2 mb-1">
            {estimatedMs > 0 && (
                <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-all duration-300 ${barColor}`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
            )}
        </div>
    );
}

function FooterEffort({ task, column }) {
    if (task.itemType === 'milestone') {
        return (
            <span className="text-xs font-bold text-[#03488a] dark:text-[#a7c8ff]">
                {task.completedChildTasks ?? 0}/{task.totalChildTasks ?? 0} Tasks ({task.progress ?? 0}%)
            </span>
        );
    }

    const { footerIcon, footerColor, footerLabel } = column;
    const estimatedMs = parseEstimatedToMs(task.estimatedTime);
    const actual = task.actualTimeMs || 0;
    const overrun = estimatedMs > 0 && actual > estimatedMs;

    if (footerLabel) {
        return (
            <span className={`flex items-center gap-1 text-sm font-bold ${footerColor}`}>
                <span
                    className="material-symbols-outlined"
                    style={{ fontFamily: 'Material Symbols Outlined', fontSize: 13 }}
                    aria-hidden="true"
                >
                    {footerIcon}
                </span>
                {footerLabel}
            </span>
        );
    }

    const effortText = task.estimatedTime
        ? actual > 0
            ? `${formatMs(actual)} / ${task.estimatedTime}`
            : `Est: ${task.estimatedTime}`
        : actual > 0
            ? formatMs(actual)
            : null;

    if (!effortText) return null;

    return (
        <span className={`flex items-center gap-1 text-sm font-bold ${overrun ? 'text-red-500' : footerColor}`}>
            <span
                className="material-symbols-outlined"
                style={{ fontFamily: 'Material Symbols Outlined', fontSize: 13 }}
                aria-hidden="true"
            >
                {footerIcon}
            </span>
            {effortText}
        </span>
    );
}

export default function KanbanCard({
    task,
    column,
    isDragging = false,
    onDragStart,
    onDragEnd,
    onCardClick,
}) {
    const priority = task.priority || 'Medium';
    const badge = PRIORITY_BADGE[priority] || PRIORITY_BADGE.Medium;
    const isCompleted = task.status === 'Completed';
    const isOnHold = task.status === 'On Hold';
    const isCancelled = task.status === 'Cancelled';
    const isDimmed = isOnHold || isCancelled;
    const isMilestone = task.itemType === 'milestone';

    return (
        <div
            draggable
            onDragStart={(e) => onDragStart(e, task)}
            onDragEnd={onDragEnd}
            onClick={() => onCardClick?.(task)}
            className={`
                group relative bg-white dark:bg-[#1e293b] p-3 rounded-xl
                border ${column.cardBorder}
                shadow-sm
                cursor-grab active:cursor-grabbing
                transition-all duration-200
                hover:shadow-md
                hover:-translate-y-0.5
                ${isDragging ? 'opacity-40 scale-95 rotate-1 shadow-xl' : ''}
                ${isDimmed ? 'opacity-75 hover:opacity-100' : ''}
            `}
        >
            {/* Drag indicator */}
            <span
                className="
                    absolute top-3 right-3
                    material-symbols-outlined
                    text-[#c2c6d2] dark:text-slate-600
                    group-hover:text-[#28b5fd] transition-colors
                    select-none pointer-events-none
                "
                style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18 }}
                aria-hidden="true"
            >
                drag_indicator
            </span>

            {/* Badges */}
            <div className="mb-3 flex items-center gap-1.5 flex-wrap">
                {isMilestone ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-black uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300 border border-amber-300 dark:border-amber-800">
                        <span className="text-amber-500 font-bold">◆</span> Milestone
                    </span>
                ) : (
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide ${badge.bg} ${badge.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${badge.dot} inline-block`} />
                        {priority}
                    </span>
                )}
            </div>

            {/* Title */}
            <h3
                className={`
                    text-sm font-semibold leading-tight mb-1 pr-6 flex items-center gap-1.5
                    ${isCompleted || isCancelled
                        ? 'line-through text-[#737782] dark:text-slate-500'
                        : 'text-[#191c1d] dark:text-white group-hover:text-[#03488a] dark:group-hover:text-[#a7c8ff]'
                    }
                    transition-colors
                `}
            >
                {isMilestone && <span className="text-amber-500 font-black shrink-0">◆</span>}
                <span>{task.title}</span>
            </h3>

            {/* Task ID */}
            {task.taskId && (
                <p className="text-xs font-mono text-[#737782] dark:text-slate-500 mb-2">
                    {task.taskId}
                </p>
            )}

            {/* Project tag */}
            {task.project?.name && (
                <span className="inline-block text-xs font-bold text-[#424750] dark:text-slate-300 bg-[#f3f4f5] dark:bg-[#334155] px-2 py-0.5 rounded mb-2">
                    {task.project.name}
                </span>
            )}

            {/* Effort / Progress bar */}
            <EffortBar
                estimatedTime={task.estimatedTime}
                actualTimeMs={task.actualTimeMs}
                status={task.status}
                isMilestone={isMilestone}
                progress={task.progress}
            />

            {/* Footer */}
            <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-[#f3f4f5] dark:border-slate-700/60">
                <AssigneeCell assignee={isMilestone ? (task.milestoneOwner || task.assignee) : task.assignee} />
                <FooterEffort task={task} column={column} />
            </div>
        </div>
    );
}
